import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/chat`;

const STORAGE_KEY = "reformity.chat.session_id";

const WELCOME_MESSAGE = {
    role: "assistant",
    content:
        "Hi — I'm Reformity's assistant. I can help with quick questions about fencing, decks, driveways, landscaping, or renovations across the GTA, and set you up with a free quote when you're ready. What's on your mind?",
    timestamp: null,
    intro: true,
};

const safeGetStored = () => {
    try {
        return localStorage.getItem(STORAGE_KEY) || null;
    } catch {
        return null;
    }
};

const safeSetStored = (val) => {
    try {
        if (val) localStorage.setItem(STORAGE_KEY, val);
        else localStorage.removeItem(STORAGE_KEY);
    } catch {
        /* ignore */
    }
};

export default function useChatSession({ initialPage = "" } = {}) {
    const [sessionId, setSessionId] = useState(safeGetStored);
    const [messages, setMessages] = useState([WELCOME_MESSAGE]);
    const [extractedContext, setExtractedContext] = useState(null);
    const [handoffService, setHandoffService] = useState(null);
    const [sending, setSending] = useState(false);
    const [sessionStatus, setSessionStatus] = useState("active");
    const [error, setError] = useState("");
    const initRef = useRef(false);

    // Bootstrap session.  Either resume existing or lazy-create on first send.
    useEffect(() => {
        if (initRef.current) return;
        initRef.current = true;
        const existing = safeGetStored();
        if (existing) {
            (async () => {
                try {
                    const res = await axios.get(`${API}/session/${existing}`);
                    const data = res.data;
                    const history = Array.isArray(data?.messages) ? data.messages : [];
                    setMessages([WELCOME_MESSAGE, ...history]);
                    setSessionStatus(data?.status || "active");
                    setExtractedContext(data?.extracted_context || null);
                    setSessionId(existing);
                } catch {
                    // Stale or deleted — clear and start fresh on next send.
                    safeSetStored(null);
                    setSessionId(null);
                }
            })();
        }
    }, []);

    const ensureSession = useCallback(async () => {
        if (sessionId) return sessionId;
        const res = await axios.post(`${API}/session`, { initial_page: initialPage });
        const id = res?.data?.session_id;
        if (!id) throw new Error("Failed to start chat session.");
        safeSetStored(id);
        setSessionId(id);
        return id;
    }, [sessionId, initialPage]);

    const sendMessage = useCallback(
        async (text) => {
            const trimmed = (text || "").trim();
            if (!trimmed || sending || sessionStatus !== "active") return;
            setSending(true);
            setError("");
            setHandoffService(null);

            // Optimistic add user message
            const userEntry = {
                role: "user",
                content: trimmed,
                timestamp: new Date().toISOString(),
            };
            setMessages((prev) => [...prev, userEntry]);

            try {
                const id = await ensureSession();
                const res = await axios.post(
                    `${API}/message`,
                    { session_id: id, message: trimmed },
                    { timeout: 30000 },
                );
                const data = res.data || {};
                const assistantEntry = {
                    role: "assistant",
                    content: data.message || "",
                    timestamp: new Date().toISOString(),
                };
                setMessages((prev) => [...prev, assistantEntry]);
                if (data.extracted_context) setExtractedContext(data.extracted_context);
                if (data.suggest_quote_handoff && data.handoff_service) {
                    setHandoffService(data.handoff_service);
                }
                if (data.status === "degraded") {
                    setError(
                        "Connection hiccup — answer may be limited. Try again or call 437-241-6287.",
                    );
                }
            } catch (err) {
                const status = err?.response?.status;
                const detail = err?.response?.data?.detail;
                if (status === 429) {
                    const msg =
                        typeof detail === "string"
                            ? detail
                            : "You've reached the chat limit. Please call us at 437-241-6287.";
                    setMessages((prev) => [
                        ...prev,
                        {
                            role: "assistant",
                            content: msg,
                            timestamp: new Date().toISOString(),
                            system: true,
                        },
                    ]);
                    setSessionStatus("rate_limited");
                } else {
                    setError(
                        "Connection hiccup — please try again or call 437-241-6287.",
                    );
                }
            } finally {
                setSending(false);
            }
        },
        [ensureSession, sending, sessionStatus],
    );

    const clearSession = useCallback(() => {
        safeSetStored(null);
        setSessionId(null);
        setMessages([WELCOME_MESSAGE]);
        setExtractedContext(null);
        setHandoffService(null);
        setSessionStatus("active");
        setError("");
    }, []);

    return {
        sessionId,
        messages,
        extractedContext,
        handoffService,
        sending,
        sessionStatus,
        error,
        sendMessage,
        clearSession,
    };
}
